// Single Responsibility: handles auth-specific data operations.
// Depends on lib/firebase/auth — not on 'firebase/auth' directly.

import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  updateProfile,
  type User,
} from 'firebase/auth'
import { auth } from '@/lib/firebase/auth'
import { toUserFacingError } from '@/lib/errors'
import type { SignInFormValues, SignUpFormValues } from '../types'

  const googleProvider = new GoogleAuthProvider()

export const authFeatureService = {
  async signIn({ email, password }: SignInFormValues): Promise<User> {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password)
      return user
    } catch (error) {
      throw toUserFacingError(error, 'We could not sign you in. Please check your details and try again.', 'auth/sign-in')
    }
  },

  async signInWithGoogle(): Promise<User> {
    try {
      const { user } = await signInWithPopup(auth, googleProvider)
      return user
    } catch (error) {
      throw toUserFacingError(error, 'We could not sign you in with Google. Please try again.', 'auth/google-sign-in')
    }
  },

  async sendPasswordReset(email: string): Promise<void> {
    await sendPasswordResetEmail(auth, email)
  },

  async signUp({ email, password, displayName }: SignUpFormValues): Promise<User> {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password)
      await updateProfile(user, { displayName })
      return user
    } catch (error) {
      throw toUserFacingError(error, 'We could not create your account. Please check your details and try again.', 'auth/sign-up')
    }
  },

  async signOut(): Promise<void> {
    try {
      await signOut(auth)
    } catch (error) {
      throw toUserFacingError(error, 'We could not sign you out. Please try again.', 'auth/sign-out')
    }
  },
}
